import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import { newsStorage, imageStorage, announcementStorage, admissionStorage } from '../../utils/storage';
import { 
  FaNewspaper, 
  FaImages, 
  FaBullhorn,
  FaSignOutAlt, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye,
  FaUpload,
  FaCalendarAlt,
  FaUser,
  FaDownload,
  FaFileImport,
  FaExclamationTriangle,
  FaGraduationCap
} from 'react-icons/fa';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [news, setNews] = useState([]);
  const [images, setImages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [editingItem, setEditingItem] = useState(null);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('adminAuth');
    if (!isAuthenticated) {
      navigate('/admin/AdminLogin');
    }
  }, [navigate]);

  // Load data from storage
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load data directly from storage without hardcoded samples
      const storedNews = newsStorage.getAllNews();
      const storedImages = await imageStorage.getAllImages();
      const storedAnnouncements = announcementStorage.getAllAnnouncements();
      const storedAdmissions = await admissionStorage.getAllAdmissions();
      
      console.log('📊 Loading data from storage:');
      console.log('News:', storedNews.length, 'items');
      console.log('Images:', storedImages.length, 'items');
      console.log('Announcements:', storedAnnouncements.length, 'items');
      console.log('Admissions:', Array.isArray(storedAdmissions) ? storedAdmissions.length : 0, 'items');
      
      setNews(storedNews);
      setImages(storedImages);
      setAnnouncements(storedAnnouncements);
      setAdmissions(Array.isArray(storedAdmissions) ? storedAdmissions : []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };

  const handleAdd = () => {
    setModalType('add');
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalType('edit');
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async (id, type) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        let success = false;
        if (type === 'news') {
          success = newsStorage.deleteNews(id);
          if (success) {
            setNews(news.filter(item => item.id !== id));
          }
        } else if (type === 'images') {
          success = await imageStorage.deleteImage(id);
          if (success) {
            setImages(images.filter(item => item.id !== id));
          }
        } else if (type === 'announcements') {
          success = announcementStorage.deleteAnnouncement(id);
          if (success) {
            setAnnouncements(announcements.filter(item => item.id !== id));
          }
        } else if (type === 'admissions') {
          success = await admissionStorage.deleteAdmission(id);
          if (success) {
            setAdmissions(admissions.filter(item => item.id !== id));
          }
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSave = async (formData) => {
    try {
      if (modalType === 'add') {
        const newItem = {
          ...formData,
          date: new Date().toISOString().split('T')[0],
          author: 'Admin'
        };
        
        // For images, map 'image' field to 'url' field
        if (activeTab === 'images' && formData.image) {
          newItem.url = formData.image;
          delete newItem.image;
        }
        
        // For admissions, keep image field
        if (activeTab === 'admissions' && formData.image) {
          newItem.image = formData.image;
        }
        
        if (activeTab === 'news') {
          const savedItem = newsStorage.saveNews(newItem);
          setNews([savedItem, ...news]);
        } else if (activeTab === 'images') {
          const savedItem = await imageStorage.saveImage(newItem);
          setImages([savedItem, ...images]);
        } else if (activeTab === 'announcements') {
          const savedItem = announcementStorage.saveAnnouncement(newItem);
          setAnnouncements([savedItem, ...announcements]);
        } else if (activeTab === 'admissions') {
          const savedItem = await admissionStorage.saveAdmission(newItem);
          setAdmissions([savedItem, ...admissions]);
        }
      } else {
        const updatedItem = {
          ...editingItem,
          ...formData,
          updatedAt: new Date().toISOString()
        };
        
        // For images, map 'image' field to 'url' field
        if (activeTab === 'images' && formData.image) {
          updatedItem.url = formData.image;
          delete updatedItem.image;
        }
        
        // For admissions, keep image field
        if (activeTab === 'admissions' && formData.image) {
          updatedItem.image = formData.image;
        }
        
        if (activeTab === 'news') {
          const savedItem = newsStorage.saveNews(updatedItem);
          setNews(news.map(item => 
            item.id === editingItem.id ? savedItem : item
          ));
        } else if (activeTab === 'images') {
          const savedItem = await imageStorage.saveImage(updatedItem);
          setImages(images.map(item => 
            item.id === editingItem.id ? savedItem : item
          ));
        } else if (activeTab === 'announcements') {
          const savedItem = announcementStorage.saveAnnouncement(updatedItem);
          setAnnouncements(announcements.map(item => 
            item.id === editingItem.id ? savedItem : item
          ));
        } else if (activeTab === 'admissions') {
          const savedItem = await admissionStorage.saveAdmission(updatedItem);
          setAdmissions(admissions.map(item => 
            item.id === editingItem.id ? savedItem : item
          ));
        }
      }
      handleModalClose();
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item. Please try again.');
    }
  };

  const handleExportData = () => {
    try {
      newsStorage.exportNews();
      alert('News data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (file) {
      newsStorage.importNews(file)
        .then(() => {
          loadData(); // Reload data after import
          alert('Data imported successfully!');
        })
        .catch((error) => {
          console.error('Error importing data:', error);
          
          alert('Error importing data. Please check the file format.');
        });
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Admin Dashboard</h1>
            <p>Manage news and images for Alasar Academy</p>
          </div>
          <div className="header-right">
            <div className="header-actions">
              <button className="btn btn-outline btn-sm" onClick={handleExportData}>
                <FaDownload />
                Export
              </button>
              <label className="btn btn-outline btn-sm">
                <FaFileImport />
                Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportData}
                  style={{ display: 'none' }}
                />
              </label>
              <button className="btn btn-outline" onClick={handleLogout}>
                <FaSignOutAlt />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-sidebar">
          <div className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => setActiveTab('news')}
            >
              <FaNewspaper />
              News Management
            </button>
            <button 
              className={`nav-item ${activeTab === 'images' ? 'active' : ''}`}
              onClick={() => setActiveTab('images')}
            >
              <FaImages />
              Image Gallery
            </button>
            <button 
              className={`nav-item ${activeTab === 'announcements' ? 'active' : ''}`}
              onClick={() => setActiveTab('announcements')}
            >
              <FaBullhorn />
              Announcements
            </button>
            <button 
              className={`nav-item ${activeTab === 'admissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('admissions')}
            >
              <FaGraduationCap />
              Admissions
            </button>
          </div>
        </div>

        <div className="dashboard-main">
          <div className="main-header">
            <h2>
              {activeTab === 'news' ? 'News Management' : 
               activeTab === 'images' ? 'Image Gallery' : 
               activeTab === 'announcements' ? 'Announcements' :
               'Admissions'}
            </h2>
            <button className="btn btn-primary" onClick={handleAdd}>
              <FaPlus />
              Add {activeTab === 'news' ? 'News' : 
                   activeTab === 'images' ? 'Image' : 
                   activeTab === 'announcements' ? 'Announcement' :
                   'Admission'}
            </button>
          </div>

          <div className="main-content">
            {activeTab === 'news' ? (
              <div className="news-grid">
                {news.map(item => (
                  <div key={item.id} className="news-card">
                    <div className="card-image">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="card-content">
                      <h3>{item.title}</h3>
                      <p>{item.content}</p>
                      <div className="card-meta">
                        <span><FaCalendarAlt /> {item.date}</span>
                        <span><FaUser /> {item.author}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleDelete(item.id, 'news')}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : activeTab === 'images' ? (
              <div className="images-grid">
                {images.map(item => (
                  <div key={item.id} className="image-card">
                    <div className="card-image">
                      <img src={item.url} alt={item.title} />
                    </div>
                    <div className="card-content">
                      <h3>{item.title}</h3>
                      <div className="card-meta">
                        <span><FaCalendarAlt /> {item.date}</span>
                        <span className="category">{item.category}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleDelete(item.id, 'images')}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : activeTab === 'announcements' ? (
              <div className="announcements-grid">
                {announcements.map(item => (
                  <div key={item.id} className="announcement-card">
                    <div className="card-header">
                      <div className="priority-badge">
                        {item.priority === 'high' ? (
                          <FaExclamationTriangle className="high-priority" />
                        ) : (
                          <FaBullhorn className="normal-priority" />
                        )}
                        <span className={`priority-text ${item.priority}`}>
                          {item.priority.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="card-content">
                      <h3>{item.title}</h3>
                      <p>{item.content}</p>
                      <div className="card-meta">
                        <span><FaCalendarAlt /> {item.date}</span>
                        <span><FaUser /> {item.author}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleDelete(item.id, 'announcements')}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="news-grid">
                {admissions.map(item => (
                  <div key={item.id} className="news-card">
                    {item.imageUrl && (
                      <div className="card-image">
                        <img src={item.imageUrl} alt={item.title} />
                      </div>
                    )}
                    <div className="card-content">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <div className="card-meta">
                        <span><FaCalendarAlt /> {item.startDate} - {item.endDate}</span>
                        <span className={`status-tag ${item.status === 'Open' ? 'open' : item.status === 'Upcoming' ? 'upcoming' : 'closed'}`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleDelete(item.id, 'admissions')}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <NewsImageModal
              type={modalType}
              itemType={activeTab}
              item={editingItem}
              onSave={handleSave}
              onClose={handleModalClose}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Modal component for adding/editing news and images
const NewsImageModal = ({ type, itemType, item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    content: item?.content || '',
    description: item?.description || '',
    image: item?.image || item?.url || item?.imageUrl || '',
    category: item?.category || '',
    priority: item?.priority || '',
    startDate: item?.startDate || '',
    endDate: item?.endDate || '',
    link: item?.link || '',
    status: item?.status || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [useUrl, setUseUrl] = useState(!item?.image && !item?.imageUrl);

  const handleChange = (e) => {
    if (e.target.type === 'file') {
      const file = e.target.files[0];
      if (file) {
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If image file is uploaded, convert it to base64
    if (imageFile && !useUrl) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newFormData = {
          ...formData,
          image: reader.result,
          imageName: imageFile.name
        };
        onSave(newFormData);
      };
      reader.readAsDataURL(imageFile);
    } else {
      onSave(formData);
    }
  };

  return (
    <div className="modal">
      <div className="modal-header">
        <h3>{type === 'add' ? 'Add' : 'Edit'} {
          itemType === 'news' ? 'News' : 
          itemType === 'images' ? 'Image' : 
          itemType === 'announcements' ? 'Announcement' :
          'Admission'
        }</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="form-group">
          <label className="form-label">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="form-input"
            required
          />
        </div>

        {(itemType === 'news' || itemType === 'announcements') && (
          <div className="form-group">
            <label className="form-label">Content</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              className="form-input form-textarea"
              rows="4"
              required
            />
          </div>
        )}

        {itemType === 'admissions' && (
          <>
            <div className="form-group">
              <label className="form-label">Image Upload Method</label>
              <div className="form-radio-group">
                <label className="form-radio">
                  <input
                    type="radio"
                    name="uploadMethod"
                    value="url"
                    checked={useUrl}
                    onChange={() => setUseUrl(true)}
                  />
                  Use URL
                </label>
                <label className="form-radio">
                  <input
                    type="radio"
                    name="uploadMethod"
                    value="upload"
                    checked={!useUrl}
                    onChange={() => setUseUrl(false)}
                  />
                  Upload File
                </label>
              </div>
            </div>
            
            {useUrl ? (
              <div className="form-group">
                <label className="form-label">Image URL</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image || ''}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Upload Image</label>
                <input
                  type="file"
                  name="imageFile"
                  accept="image/*"
                  onChange={handleChange}
                  className="form-input"
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-input form-textarea"
                rows="4"
                required
              />
            </div>
          </>
        )}

        {(itemType === 'news' || itemType === 'images') && (
          <>
            <div className="form-group">
              <label className="form-label">Image Upload Method</label>
              <div className="form-radio-group">
                <label className="form-radio">
                  <input
                    type="radio"
                    name="uploadMethod"
                    value="url"
                    checked={useUrl}
                    onChange={() => setUseUrl(true)}
                  />
                  Use URL
                </label>
                <label className="form-radio">
                  <input
                    type="radio"
                    name="uploadMethod"
                    value="upload"
                    checked={!useUrl}
                    onChange={() => setUseUrl(false)}
                  />
                  Upload File
                </label>
              </div>
            </div>
            
            {useUrl ? (
              <div className="form-group">
                <label className="form-label">Image URL</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://example.com/image.jpg"
                  required={itemType === 'images'}
                />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Upload Image</label>
                <input
                  type="file"
                  name="imageFile"
                  accept="image/*"
                  onChange={handleChange}
                  className="form-input"
                  required={itemType === 'images'}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {itemType === 'images' && (
          <div className="form-group">
            <label className="form-label">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="form-input"
              required
            >
              <option value="">Select Category</option>
              <option value="Campus">Campus</option>
              <option value="Facilities">Facilities</option>
              <option value="Events">Events</option>
              <option value="Students">Students</option>
            </select>
          </div>
        )}

        {itemType === 'announcements' && (
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="form-input"
              required
            >
              <option value="">Select Priority</option>
              <option value="high">High Priority</option>
              <option value="normal">Normal Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
        )}

        {itemType === 'admissions' && (
          <>
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Link</label>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleChange}
                className="form-input"
                placeholder="https://example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Select Status</option>
                <option value="Open">Open</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {type === 'add' ? 'Add' : 'Update'} {
              itemType === 'news' ? 'News' : 
              itemType === 'images' ? 'Image' : 
              itemType === 'announcements' ? 'Announcement' :
              'Admission'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminDashboard;
